import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './ColorPicker.module.css';

// Preset colors for quick selection
const PRESET_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899',
  '#06b6d4', '#f97316', '#84cc16', '#6366f1', '#14b8a6',
  '#ef4444', '#22c55e', '#eab308', '#a855f7', '#f43f5e',
  '#0ea5e9', '#fb923c', '#4ade80', '#818cf8', '#2dd4bf',
];

export default function ColorPicker({ value, onChange, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempColor, setTempColor] = useState(value || '#3b82f6');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const colorInputRef = useRef(null);

  // Position dropdown and handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && 
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      if (buttonRef.current && isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Sync temp color when value changes externally
  useEffect(() => {
    setTempColor(value || '#3b82f6');
  }, [value]);

  const handleColorChange = (newColor) => {
    setTempColor(newColor);
    onChange({ target: { value: newColor } });
  };

  const handleSwatchClick = (color) => {
    handleColorChange(color);
  };

  const handleGradientClick = () => {
    if (colorInputRef.current) {
      colorInputRef.current.click();
    }
  };

  return (
    <div className={styles.colorPickerContainer}>
      {/* Color preview button */}
      <button
        ref={buttonRef}
        type="button"
        className={styles.colorPreview}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{ backgroundColor: value || '#3b82f6' }}
        title="Click to change color"
      >
        <span className={styles.colorValue}>{value || '#3b82f6'}</span>
      </button>

      {/* Dropdown picker - rendered via portal to escape modal constraints */}
      {isOpen && !disabled && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className={styles.pickerDropdown}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          <div className={styles.pickerHeader}>
            <span>Choose a color</span>
          </div>

          {/* Gradient color picker */}
          <div className={styles.gradientSection}>
            <div 
              className={styles.gradientPreview}
              onClick={handleGradientClick}
              style={{ backgroundColor: tempColor }}
            >
              <span className={styles.gradientLabel}>Click to open color picker</span>
              <input
                ref={colorInputRef}
                type="color"
                value={tempColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className={styles.hiddenColorInput}
              />
            </div>
            <div className={styles.hexInput}>
              <span className={styles.hashSymbol}>#</span>
              <input
                type="text"
                value={tempColor.replace('#', '')}
                onChange={(e) => {
                  const hex = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                  if (hex.length === 6) {
                    handleColorChange(`#${hex}`);
                  }
                }}
                maxLength={6}
                placeholder="3b82f6"
              />
            </div>
          </div>

          {/* Preset swatches */}
          <div className={styles.presetsSection}>
            <span className={styles.presetsLabel}>Presets</span>
            <div className={styles.swatchGrid}>
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`${styles.swatch} ${tempColor === color ? styles.swatchSelected : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleSwatchClick(color)}
                  title={color}
                />
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
